/**
 * Inventory — ahora la unidad visual es **la variedad**, no una fila por
 * (variety, stemLength).
 *
 * Cada tarjeta agrupa todas las longitudes de tallo registradas para esa
 * variedad, mostrando el total agregado en el header y, dentro, una tabla
 * con una fila por longitud. Cada fila conserva sus acciones `Mover` y
 * `Ajustar` que operan sobre el `id` del registro de inventario real
 * (`INV-XXX`). Una variedad puede tener múltiples INV-ids — uno por
 * longitud.
 *
 * Flujos nuevos:
 *   · "Nueva variedad" (top-level): crea la primera longitud de una
 *     variedad que no existe todavía en inventario.
 *   · "Añadir longitud" (dentro de la tarjeta): agrega otra longitud
 *     de tallo para una variedad existente.
 *
 * Endpoints:
 *   POST /api/inventory { variety, stemLength, stages? }  → crea el par
 *   POST /api/inventory/:id/move   { from, to, quantity }
 *   POST /api/inventory/:id/adjust { stage, delta }
 */
import { useEffect, useMemo, useState } from 'react'
import {
  ArrowRightLeft,
  Package,
  Plus,
} from 'lucide-react'
import { toast } from 'sonner'
import { ModuleShell } from '@/components/ui/ModuleShell'
import { Segmented } from '@/components/ui/Segmented'
import { FilterBar } from '@/components/ui/FilterBar'
import { SearchButton } from '@/components/ui/SearchButton'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'

/**
 * Etapas visibles en la UI. Por feedback se ocultó "En proceso" — sigue
 * existiendo en el modelo de datos para no romper inventarios viejos,
 * pero no se muestra como columna ni como opción en el modal de mover.
 * "Cuarto frío" se renombró a "Procesado".
 */
const STAGES = [
  { id: 'unprocessed', label: 'Sin procesar' },
  { id: 'cold_room', label: 'Procesado' },
]

const rowTotal = (r) =>
  (r.stages?.unprocessed?.total || 0) +
  (r.stages?.in_process?.total || 0) +
  (r.stages?.cold_room?.total || 0)

const fmtQty = (n) => Number(n || 0).toLocaleString('es-CO')

// Etapas visibles en las barras/leyendas (#38/#39). "En proceso" sigue
// oculto por feedback del cliente.
const STAGE_COLOR = { unprocessed: '#C3DB5A', cold_room: '#14502A' }

// Botón de acción compacto (Mover / Ajustar) dentro de la tarjeta #38.
const ICON_BTN = {
  width: 30,
  height: 30,
  borderRadius: 9,
  border: '1px solid #E6EFE3',
  background: '#fff',
  color: '#143A22',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flex: 'none',
}
const visUnproc = (r) => Number(r.stages?.unprocessed?.total) || 0
const visProc = (r) => Number(r.stages?.cold_room?.total) || 0
const visTotal = (r) => visUnproc(r) + visProc(r)

const STAGE_LABEL = { unprocessed: 'Sin procesar', cold_room: 'Procesado' }

const STEP_BTN = {
  width: 56,
  height: 56,
  border: 'none',
  background: '#fff',
  color: '#1E6E3A',
  fontSize: 28,
  fontWeight: 800,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
}

const QUICK_CHIP = {
  border: '1.5px solid #D7E3D2',
  background: '#fff',
  color: '#1E6E3A',
  borderRadius: 999,
  padding: '8px 14px',
  fontSize: 14,
  fontWeight: 700,
  cursor: 'pointer',
}

/** Stepper grande (− valor +), handoff #11. */
function Stepper({ value, onChange, min = 0, step = 10 }) {
  const set = (n) => onChange(Math.max(min, Math.round(n)))
  return (
    <div className="inline-flex items-center" style={{ border: '1.5px solid #E6EFE3', borderRadius: 14, overflow: 'hidden', flex: 'none' }}>
      <button type="button" onClick={() => set((Number(value) || 0) - step)} style={STEP_BTN} aria-label="Restar">−</button>
      <input
        type="number"
        value={value}
        onChange={(e) => set(Number(e.target.value) || 0)}
        style={{
          width: 96,
          height: 56,
          textAlign: 'center',
          border: 'none',
          borderLeft: '1.5px solid #E6EFE3',
          borderRight: '1.5px solid #E6EFE3',
          outline: 'none',
          fontSize: 24,
          fontWeight: 800,
          color: '#143A22',
          background: '#fff',
        }}
      />
      <button type="button" onClick={() => set((Number(value) || 0) + step)} style={STEP_BTN} aria-label="Sumar">+</button>
    </div>
  )
}

/** Botón-tarjeta de selección grande (etapa / modo). */
function BigChoice({ active, onClick, title, sub, tone = 'green' }) {
  const activeBorder = tone === 'red' ? '#C0392B' : '#033021'
  const activeBg = tone === 'red' ? '#FDECEC' : '#E3F0D5'
  return (
    <button
      type="button"
      onClick={onClick}
      className="press"
      style={{
        flex: 1,
        textAlign: 'left',
        cursor: 'pointer',
        borderRadius: 14,
        padding: '14px 16px',
        border: active ? `2px solid ${activeBorder}` : '1.5px solid #E6EFE3',
        background: active ? activeBg : '#fff',
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 800, color: '#143A22' }}>{title}</div>
      {sub != null && <div style={{ fontSize: 13, color: '#5C6B60', marginTop: 2 }}>{sub}</div>}
    </button>
  )
}

/** Etiqueta de sección de los modales de inventario. */
function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.02em', color: '#5C6B60', marginBottom: 10 }}>
      {children}
    </div>
  )
}

/** Item de leyenda (punto + etiqueta + valor) para #38/#39. */
function StageLegend({ color, label, value, big = false }) {
  return (
    <span className="inline-flex items-center" style={{ gap: big ? 9 : 7 }}>
      <span style={{ width: big ? 11 : 9, height: big ? 11 : 9, borderRadius: 999, background: color, flex: 'none' }} />
      <span style={{ fontSize: big ? 16 : 14, color: '#5C6B60', fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: big ? 16 : 14, fontWeight: 800, color: '#143A22' }}>{fmtQty(value)}</span>
    </span>
  )
}

export default function InventoryPage() {
  const { data, loading, error, refresh } = useApi('/inventory')
  const varieties = useApi('/varieties')
  // Doc: al "Añadir inventario" se debe poder vincular el ingreso a una
  // OC ya creada para que se autollenen los productos de esa OC.
  const purchases = useApi('/purchases')

  // Búsqueda por variedad + filtro por etapa con stock.
  const [query, setQuery] = useState('')
  const [stageFilter, setStageFilter] = useState('all') // all | unprocessed | cold_room

  // Acciones por-fila (operan sobre un INV-id concreto).
  const [moving, setMoving] = useState(null)
  const [adjusting, setAdjusting] = useState(null)
  // Estado controlado de los modales rediseñados (grandes y simples).
  const [adjStage, setAdjStage] = useState('unprocessed') // etapa a ajustar
  const [adjMode, setAdjMode] = useState('add') // 'add' | 'remove'
  const [adjQty, setAdjQty] = useState(0)
  const [mvFrom, setMvFrom] = useState('unprocessed') // mover desde
  const [mvQty, setMvQty] = useState(0)

  // Reset al abrir cada modal.
  useEffect(() => {
    if (adjusting) {
      setAdjStage('unprocessed')
      setAdjMode('add')
      setAdjQty(0)
    }
  }, [adjusting])
  useEffect(() => {
    if (moving) {
      setMvFrom('unprocessed')
      setMvQty(0)
    }
  }, [moving])
  // Alta de un nuevo (variety, stemLength). Si `varietyLocked` está seteado,
  // el modal deshabilita el campo de variedad — así el botón "Añadir
  // longitud" dentro de una tarjeta no permite cambiar la variedad.
  const [creating, setCreating] = useState(false)
  const [varietyLocked, setVarietyLocked] = useState(null)
  // OC seleccionada en el modal de "Añadir inventario" — autollena los
  // items pendientes de esa orden.
  const [linkedPoId, setLinkedPoId] = useState('')
  const [receivedRows, setReceivedRows] = useState([])

  // OCs en estado activo (no cancelled, no completed) — son las únicas
  // a las que tendría sentido enlazar una recepción de inventario.
  const activePOs = useMemo(
    () =>
      (purchases.data || []).filter(
        (p) => p.status !== 'cancelled' && p.status !== 'completed',
      ),
    [purchases.data],
  )

  const linkedPo = useMemo(
    () => activePOs.find((p) => p.id === linkedPoId) || null,
    [activePOs, linkedPoId],
  )

  const move = useMutation(async (id, body) =>
    apiCall(`/inventory/${id}/move`, { method: 'POST', body }),
  )
  const adjust = useMutation(async (id, body) =>
    apiCall(`/inventory/${id}/adjust`, { method: 'POST', body }),
  )
  const create = useMutation(async (payload) =>
    apiCall('/inventory', { method: 'POST', body: payload }),
  )
  // Si se recibe menos cantidad que la OC, ajustamos la OC para que el
  // pendiente por comprar refleje el faltante. Esto es un PUT sobre items[].
  const updatePO = useMutation(async ({ id, payload }) =>
    apiCall(`/purchases/${id}`, { method: 'PUT', body: payload }),
  )
  // Marca la OC como recibida (at_farm) sin tocar inventario (ya lo
  // ajustamos manualmente acá) y dispara la transición de las ventas
  // ligadas a "En procesamiento".
  const markPOReceived = useMutation(async (id) =>
    apiCall(`/purchases/${id}/mark-received`, { method: 'POST' }),
  )

  // Agrupar por variedad. Cada grupo conserva el detalle por longitud
  // y un total agregado para mostrar en el header de la tarjeta.
  //
  // Doc: "si no hay inventario, debería desaparecer el tipo de producto y
  // todas las longitudes". Por eso ocultamos filas con total 0 y, si una
  // variedad queda sin filas, también se oculta del listado.
  const groups = useMemo(() => {
    const by = new Map()
    for (const r of data || []) {
      const key = r.variety || '—'
      if (!by.has(key)) by.set(key, [])
      by.get(key).push(r)
    }
    const out = []
    for (const [variety, allRows] of by.entries()) {
      // Si no hay stock, no se muestra: ocultamos las longitudes en 0 y, si
      // una variedad queda sin filas, también se oculta. (Para reponer una
      // longitud oculta, "Añadir longitud" detecta que ya existe y abre
      // "Ajustar" sobre ella — ver onCreate.)
      const rows = allRows.filter((r) => rowTotal(r) > 0)
      if (rows.length === 0) continue
      rows.sort((a, b) => Number(a.stemLength) - Number(b.stemLength))
      const total = rows.reduce((acc, r) => acc + rowTotal(r), 0)
      out.push({ variety, rows, total })
    }
    out.sort((a, b) => a.variety.localeCompare(b.variety, 'es'))
    return out
  }, [data])

  // Totales globales por etapa visible (para el resumen #39).
  const summary = useMemo(() => {
    let u = 0
    let p = 0
    for (const g of groups) {
      for (const r of g.rows) {
        u += visUnproc(r)
        p += visProc(r)
      }
    }
    return { u, p, total: u + p }
  }, [groups])

  // Variedades mostradas según búsqueda + etapa (el resumen #39 sigue global).
  const displayGroups = useMemo(() => {
    const q = query.trim().toLowerCase()
    return groups.filter((g) => {
      if (q && !String(g.variety).toLowerCase().includes(q)) return false
      if (stageFilter !== 'all') {
        const has = g.rows.some((r) =>
          (stageFilter === 'unprocessed' ? visUnproc(r) : visProc(r)) > 0,
        )
        if (!has) return false
      }
      return true
    })
  }, [groups, query, stageFilter])

  const onMove = async (e) => {
    e.preventDefault()
    const qty = Number(mvQty) || 0
    const to = mvFrom === 'unprocessed' ? 'cold_room' : 'unprocessed'
    const available = Number(moving?.stages?.[mvFrom]?.total) || 0
    if (qty <= 0) return toast.error('Indica cuántos tallos quieres mover')
    if (qty > available) {
      return toast.error(
        `Solo hay ${fmtQty(available)} en ${STAGE_LABEL[mvFrom]} para mover`,
      )
    }
    try {
      await move.mutate(moving.id, { from: mvFrom, to, quantity: qty })
      toast.success('Movimiento registrado')
      setMoving(null)
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onAdjust = async (e) => {
    e.preventDefault()
    const qty = Number(adjQty) || 0
    if (qty <= 0) return toast.error('Indica una cantidad mayor a 0')
    const current = Number(adjusting?.stages?.[adjStage]?.total) || 0
    if (adjMode === 'remove' && qty > current) {
      return toast.error(
        `Solo hay ${fmtQty(current)} en ${STAGE_LABEL[adjStage]} para quitar`,
      )
    }
    const delta = adjMode === 'remove' ? -qty : qty
    try {
      await adjust.mutate(adjusting.id, { stage: adjStage, delta })
      toast.success('Inventario ajustado')
      setAdjusting(null)
      refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  /**
   * Sube/ajusta inventario por cada fila recibida. Si la OC indicaba
   * más cantidad que la efectivamente recibida, ajustamos los items[] de
   * la OC para reflejar el faltante en el pendiente por comprar.
   */
  const submitReceiveFromPO = async () => {
    if (!linkedPo) return
    const rowsWithQty = receivedRows.filter((r) => Number(r.received) > 0)
    if (rowsWithQty.length === 0) {
      return toast.error('Indica cantidad recibida para al menos un item')
    }
    try {
      // 1) Para cada item recibido, hacer create (si no existe par
      // variety+length) o adjust (si ya existe). Lo enviamos como
      // 'cold_room' (Procesado) directamente: el doc unificó las etapas
      // y el producto recibido de OC se considera ya procesado para
      // despachar. Si después hay que mover una parte a Sin procesar
      // por algún motivo de calidad, se hace manual con "Mover".
      for (const r of rowsWithQty) {
        const existing = (data || []).find(
          (i) =>
            i.variety === r.variety &&
            Number(i.stemLength) === Number(r.stemLength),
        )
        const qty = Number(r.received) || 0
        if (existing) {
          await adjust.mutate(existing.id, { stage: 'cold_room', delta: qty })
        } else {
          await create.mutate({
            variety: r.variety,
            stemLength: Number(r.stemLength),
            stages: { cold_room: { total: qty } },
          })
        }
      }
      // 2) Si hubo faltantes (received < ordered), actualizar la OC para
      //    que la diferencia vuelva al pendiente por comprar. IMPORTANTE:
      //    consultamos `receivedRows` completo (no rowsWithQty filtrado),
      //    porque si el operario marcó received=0 para un producto que
      //    SÍ estaba en la OC, ese item debe quedar quantity=0 — no
      //    preservar la cantidad original, que daría la falsa impresión
      //    de haber llegado completo y ocultaría el faltante en el board
      //    de "Pendiente por comprar". Además guardamos `orderedQuantity`
      //    para no perder la cantidad pedida originalmente.
      const newItems = (linkedPo.items || []).map((it) => {
        const row = receivedRows.find(
          (r) =>
            r.variety === it.variety &&
            Number(r.stemLength) === Number(it.stemLength),
        )
        const ordered = Number(it.orderedQuantity ?? it.quantity) || 0
        const recv = row
          ? Math.min(Number(row.received) || 0, ordered)
          : 0
        return {
          ...it,
          orderedQuantity: ordered,
          quantity: recv,
          receivedAt: new Date().toISOString(),
        }
      })
      const shortfall = newItems.some(
        (it) =>
          Number(it.quantity) < Number(it.orderedQuantity ?? it.quantity),
      )
      if (shortfall) {
        await updatePO.mutate({
          id: linkedPo.id,
          payload: {
            items: newItems,
            partialReceivedAt: new Date().toISOString(),
          },
        })
        toast.success(
          `Recepción parcial: la OC ${linkedPo.id} se ajustó y el faltante vuelve a Pendiente por comprar.`,
        )
      } else {
        toast.success(`Recepción registrada para OC ${linkedPo.id}`)
      }
      // 3) Marcar la OC como recibida y disparar la transición de las
      //    ventas ligadas a "En procesamiento" en el dashboard. Va
      //    DESPUÉS de los adjustments para que no duplique inventario.
      try {
        await markPOReceived.mutate(linkedPo.id)
      } catch (err) {
        // No bloquear el flujo si esto falla — el inventario ya quedó
        // bien; solo no se actualizará el status del pedido.
        console.warn('mark-received failed:', err?.message)
      }
      setCreating(false)
      setLinkedPoId('')
      setReceivedRows([])
      refresh()
      purchases.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onCreate = async (e) => {
    e.preventDefault()
    // Modo OC: cuando hay una OC vinculada, ignoramos el form clásico y
    // disparamos la recepción multi-item.
    if (linkedPoId) {
      return submitReceiveFromPO()
    }

    const fd = Object.fromEntries(new FormData(e.currentTarget).entries())
    const variety = (varietyLocked || fd.variety || '').trim()
    const stemLength = Number(fd.stemLength)
    if (!variety) return toast.error('Variedad requerida')
    if (!Number.isFinite(stemLength) || stemLength <= 0) {
      return toast.error('Longitud inválida')
    }
    // Si el par variedad/longitud ya existe, no es un error: abrimos
    // "Ajustar" sobre esa fila para que el usuario le sume stock. Esto evita
    // el caso confuso en que la longitud existía pero estaba oculta (0 tallos)
    // y aun así bloqueaba el alta.
    const clash = (data || []).find(
      (r) => r.variety === variety && Number(r.stemLength) === stemLength,
    )
    if (clash) {
      setCreating(false)
      setVarietyLocked(null)
      setLinkedPoId('')
      setReceivedRows([])
      setAdjusting(clash)
      toast.message(`${variety} ${stemLength}cm ya existe — súmale stock aquí`)
      return
    }
    // Cantidad inicial obligatoria: una longitud con 0 tallos quedaría oculta
    // del listado (se ocultan filas sin stock), así que el alta parecería "no
    // hacer nada". Exigimos al menos 1 tallo entre las dos etapas visibles.
    const initialQty =
      Math.max(0, Number(fd.unprocessed) || 0) + Math.max(0, Number(fd.cold_room) || 0)
    if (initialQty <= 0) {
      return toast.error(
        'Indica una cantidad inicial (Sin procesar o Procesado) para registrar la longitud.',
      )
    }
    const payload = {
      variety,
      stemLength,
      stages: {
        unprocessed: { total: Math.max(0, Number(fd.unprocessed) || 0) },
        // El cliente pidió ocultar "En proceso" — al crear, ese stage
        // siempre arranca en 0 (la migración a "Procesado" requiere el
        // workflow normal de Mover).
        in_process: { total: 0 },
        cold_room: { total: Math.max(0, Number(fd.cold_room) || 0) },
      },
    }
    try {
      await create.mutate(payload)
      toast.success(
        varietyLocked
          ? `${variety} ${stemLength}cm añadido`
          : `Variedad ${variety} creada`,
      )
      setCreating(false)
      setVarietyLocked(null)
      refresh()
      varieties.refresh()
    } catch (err) {
      if (err.message === 'duplicate_variety_length') {
        toast.error('Ese par variedad/longitud ya existe')
      } else {
        toast.error(err.message)
      }
    }
  }

  // Sugerencias de variedades: unión del catálogo remoto + lo que ya existe
  // en inventario. Evita dropdowns vacíos si la variedad aún no está en el
  // seed pero sí hay un datalist de autocompletar.
  const varietySuggestions = useMemo(() => {
    const set = new Set(varieties.data || [])
    for (const r of data || []) if (r.variety) set.add(r.variety)
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'))
  }, [varieties.data, data])

  return (
    <div>
      <ModuleShell
        title="Inventario"
        subtitle="Variedades agrupadas — cada tarjeta desglosa por longitud de tallo"
        icon={Package}
        action={
          <div className="flex items-center gap-3">
            <SearchButton value={query} onChange={setQuery} placeholder="Buscar variedad…" />
            <Button
              className="!px-3 sm:!px-6"
              onClick={() => {
                setVarietyLocked(null)
                setLinkedPoId('')
                setReceivedRows([])
                setCreating(true)
              }}
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Añadir inventario</span>
            </Button>
          </div>
        }
        filters={
          <FilterBar left={
            <Segmented
              value={stageFilter}
              onChange={setStageFilter}
              options={[
                { value: 'all', label: 'Todas' },
                { value: 'unprocessed', label: 'Sin procesar' },
                { value: 'cold_room', label: 'Procesado' },
              ]}
            />
          } />
        }
      >
        {loading && <p className="text-expo-muted">Cargando…</p>}
        {error && <p className="text-red-600">Error: {error.message}</p>}

        {data &&
        (groups.length === 0 ? (
          <div className="rounded-2xl border border-expo-line bg-white p-12 text-center text-expo-muted text-sm">
            Sin inventario — empieza creando una variedad
          </div>
        ) : (
          <div className="space-y-6">
            {/* #39 · Resumen de inventario */}
            <div style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 22, padding: '30px 32px' }}>
              <div className="flex items-start justify-between gap-6 flex-wrap" style={{ marginBottom: 22 }}>
                <div>
                  <div className="font-display" style={{ fontSize: 28, fontWeight: 800, color: '#143A22', letterSpacing: '-0.01em', lineHeight: 1.05 }}>
                    Inventario actual por variedad y longitud
                  </div>
                  <div style={{ fontSize: 15, color: '#8B9B8F', fontWeight: 500, marginTop: 4 }}>
                    Estado de procesamiento del stock disponible
                  </div>
                </div>
                <div style={{ textAlign: 'right', flex: 'none' }}>
                  <div className="font-display" style={{ fontSize: 40, fontWeight: 800, color: '#033021', lineHeight: 1 }}>
                    {fmtQty(summary.total)}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', color: '#9BA89B', marginTop: 4 }}>
                    TALLOS EN TOTAL
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', height: 14, borderRadius: 999, overflow: 'hidden', marginBottom: 18, background: '#ECECEA' }}>
                {summary.total > 0 && (
                  <>
                    <div style={{ width: `${(summary.u / summary.total) * 100}%`, background: STAGE_COLOR.unprocessed }} />
                    <div style={{ width: `${(summary.p / summary.total) * 100}%`, background: STAGE_COLOR.cold_room }} />
                  </>
                )}
              </div>
              <div className="flex flex-wrap" style={{ gap: 26 }}>
                <StageLegend big color={STAGE_COLOR.unprocessed} label="Sin procesar" value={summary.u} />
                <StageLegend big color={STAGE_COLOR.cold_room} label="Procesado" value={summary.p} />
              </div>
            </div>

            {/* #38 · Tarjetas de variedad */}
            {displayGroups.length === 0 ? (
              <div className="rounded-2xl border border-expo-line bg-white p-10 text-center text-expo-muted text-sm">
                Sin variedades que coincidan con la búsqueda.
              </div>
            ) : (
            <div className="grid gap-5 grid-cols-1 lg:grid-cols-2">
              {displayGroups.map((g) => {
                const vTotal = g.rows.reduce((a, r) => a + visTotal(r), 0)
                return (
                  <div key={g.variety} style={{ background: '#fff', border: '1px solid #E6EFE3', borderRadius: 20, overflow: 'hidden' }}>
                    <div
                      className="flex items-center justify-between"
                      style={{ padding: '20px 22px', borderBottom: '1px solid #EEF2EC' }}
                    >
                      <div className="flex items-center min-w-0" style={{ gap: 15 }}>
                        <div
                          className="flex items-center justify-center flex-none"
                          style={{ width: 50, height: 50, borderRadius: 14, background: '#033021', color: '#C3DB5A', fontWeight: 800, fontSize: 21 }}
                        >
                          {String(g.variety).charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-display truncate" style={{ fontSize: 25, fontWeight: 800, color: '#143A22', lineHeight: 1.05 }}>
                            {g.variety}
                          </div>
                          <div style={{ fontSize: 14, color: '#7E9285', fontWeight: 600 }}>
                            {g.rows.length} longitud{g.rows.length === 1 ? '' : 'es'}
                          </div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', flex: 'none' }}>
                        <div className="font-display" style={{ fontSize: 32, fontWeight: 800, color: '#033021', lineHeight: 1 }}>
                          {fmtQty(vTotal)}
                        </div>
                        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', color: '#9BA89B' }}>TALLOS</div>
                      </div>
                    </div>

                    <div style={{ padding: '20px 22px' }}>
                      {g.rows.map((r, idx) => {
                        const u = visUnproc(r)
                        const p = visProc(r)
                        const t = u + p
                        return (
                          <div key={r.id} style={{ marginBottom: idx === g.rows.length - 1 ? 0 : 20 }}>
                            <div className="flex items-center justify-between" style={{ marginBottom: 11, gap: 12 }}>
                              <span className="font-display" style={{ fontSize: 19, fontWeight: 800, color: '#143A22' }}>
                                {r.stemLength}cm
                              </span>
                              <div className="flex items-center" style={{ gap: 10 }}>
                                <span className="font-display" style={{ fontSize: 19, fontWeight: 800, color: '#033021' }}>
                                  {fmtQty(t)}
                                </span>
                                <button type="button" onClick={() => setMoving(r)} title="Mover" style={ICON_BTN}>
                                  <ArrowRightLeft size={14} />
                                </button>
                                <button type="button" onClick={() => setAdjusting(r)} title="Ajustar" style={ICON_BTN}>
                                  <Plus size={14} />
                                </button>
                              </div>
                            </div>
                            <div style={{ display: 'flex', height: 11, borderRadius: 999, overflow: 'hidden', marginBottom: 13, background: '#ECECEA' }}>
                              {t > 0 && (
                                <>
                                  {u > 0 && <div style={{ width: `${(u / t) * 100}%`, background: STAGE_COLOR.unprocessed }} />}
                                  {p > 0 && <div style={{ width: `${(p / t) * 100}%`, background: STAGE_COLOR.cold_room }} />}
                                </>
                              )}
                            </div>
                            <div className="flex flex-wrap" style={{ gap: 18 }}>
                              {u > 0 && <StageLegend color={STAGE_COLOR.unprocessed} label="Sin procesar" value={u} />}
                              {p > 0 && <StageLegend color={STAGE_COLOR.cold_room} label="Procesado" value={p} />}
                              {t === 0 && <span style={{ fontSize: 13, color: '#8B9B8F' }}>Sin stock</span>}
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <div style={{ padding: '0 22px 22px' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setVarietyLocked(g.variety)
                          setLinkedPoId('')
                          setReceivedRows([])
                          setCreating(true)
                        }}
                        style={{ background: '#F4F9EC', color: '#1E6E3A', border: '1.5px dashed #BBD894', borderRadius: 14, padding: 15, fontFamily: 'inherit', fontSize: 15, fontWeight: 700, cursor: 'pointer', width: '100%' }}
                      >
                        + Añadir longitud
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
            )}
          </div>
        ))}
      </ModuleShell>

      {/* ── Modal: Mover entre etapas ─────────────────────────────── */}
      <Modal
        open={!!moving}
        onClose={() => setMoving(null)}
        title={moving ? `Mover · ${moving.variety} ${moving.stemLength}cm` : ''}
        subtitle="Mueve tallos entre etapas"
      >
        {moving && (() => {
          const to = mvFrom === 'unprocessed' ? 'cold_room' : 'unprocessed'
          const available = Number(moving.stages?.[mvFrom]?.total) || 0
          return (
            <form onSubmit={onMove} className="space-y-5">
              <div>
                <FieldLabel>Mover desde…</FieldLabel>
                <div className="flex gap-3">
                  {STAGES.map((s) => (
                    <BigChoice
                      key={s.id}
                      active={mvFrom === s.id}
                      onClick={() => setMvFrom(s.id)}
                      title={s.label}
                      sub={`Disponible: ${fmtQty(moving.stages?.[s.id]?.total ?? 0)}`}
                    />
                  ))}
                </div>
              </div>

              <div style={{ fontSize: 15, color: '#5C6B60' }}>
                Pasará a <b style={{ color: '#143A22' }}>{STAGE_LABEL[to]}</b>
              </div>

              <div>
                <FieldLabel>¿Cuántos tallos mover?</FieldLabel>
                <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
                  <Stepper value={mvQty} onChange={setMvQty} />
                  <div className="flex flex-wrap" style={{ gap: 8 }}>
                    {[50, 100].map((n) => (
                      <button key={n} type="button" style={QUICK_CHIP} onClick={() => setMvQty(n)}>
                        {n}
                      </button>
                    ))}
                    {available > 0 && (
                      <button type="button" style={QUICK_CHIP} onClick={() => setMvQty(available)}>
                        Todo ({fmtQty(available)})
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex gap-3" style={{ paddingTop: 4 }}>
                <Button type="button" variant="secondary" size="lg" style={{ flex: 1 }} onClick={() => setMoving(null)}>
                  Cancelar
                </Button>
                <Button type="submit" size="lg" style={{ flex: 1 }} disabled={move.loading || Number(mvQty) <= 0}>
                  {move.loading ? 'Moviendo…' : `Mover ${fmtQty(mvQty)} tallos`}
                </Button>
              </div>
            </form>
          )
        })()}
      </Modal>

      {/* ── Modal: Ajustar stock ──────────────────────────────────── */}
      <Modal
        open={!!adjusting}
        onClose={() => setAdjusting(null)}
        title={adjusting ? `Ajustar · ${adjusting.variety} ${adjusting.stemLength}cm` : ''}
        subtitle="Agrega o quita tallos de una etapa"
      >
        {adjusting && (() => {
          const current = Number(adjusting.stages?.[adjStage]?.total) || 0
          const q = Number(adjQty) || 0
          const result = adjMode === 'remove' ? Math.max(0, current - q) : current + q
          return (
            <form onSubmit={onAdjust} className="space-y-5">
              <div>
                <FieldLabel>¿A qué etapa?</FieldLabel>
                <div className="flex gap-3">
                  {STAGES.map((s) => (
                    <BigChoice
                      key={s.id}
                      active={adjStage === s.id}
                      onClick={() => setAdjStage(s.id)}
                      title={s.label}
                      sub={`Actual: ${fmtQty(adjusting.stages?.[s.id]?.total ?? 0)}`}
                    />
                  ))}
                </div>
              </div>

              <div>
                <FieldLabel>¿Qué quieres hacer?</FieldLabel>
                <div className="flex gap-3">
                  <BigChoice active={adjMode === 'add'} onClick={() => setAdjMode('add')} title="＋  Agregar" />
                  <BigChoice active={adjMode === 'remove'} onClick={() => setAdjMode('remove')} title="－  Quitar" tone="red" />
                </div>
              </div>

              <div>
                <FieldLabel>¿Cuántos tallos?</FieldLabel>
                <div className="flex items-center flex-wrap" style={{ gap: 12 }}>
                  <Stepper value={adjQty} onChange={setAdjQty} />
                  <div className="flex flex-wrap" style={{ gap: 8 }}>
                    {[50, 100, 500].map((n) => (
                      <button key={n} type="button" style={QUICK_CHIP} onClick={() => setAdjQty(n)}>
                        +{n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 15, color: '#5C6B60' }}>
                {STAGE_LABEL[adjStage]}: <b style={{ color: '#143A22' }}>{fmtQty(current)}</b>{' → '}
                <b style={{ color: adjMode === 'remove' ? '#C0392B' : '#1E6E3A' }}>{fmtQty(result)}</b>
              </div>

              <div className="flex gap-3" style={{ paddingTop: 4 }}>
                <Button type="button" variant="secondary" size="lg" style={{ flex: 1 }} onClick={() => setAdjusting(null)}>
                  Cancelar
                </Button>
                <Button type="submit" size="lg" style={{ flex: 1 }} disabled={adjust.loading || q <= 0}>
                  {adjust.loading
                    ? 'Aplicando…'
                    : `${adjMode === 'add' ? 'Agregar' : 'Quitar'} ${fmtQty(q)} tallos`}
                </Button>
              </div>
            </form>
          )
        })()}
      </Modal>

      {/* ── Modal: Añadir inventario ─────────────────────────────────
       *
       * Doc: este modal sustituye al antiguo "Nueva variedad". Permite
       * dos modos:
       *   1) Vincular a OC: dropdown de OC activas → autollena los
       *      items, el usuario indica cuánto recibió por cada uno. Si
       *      recibió menos, la OC se ajusta para devolver el faltante a
       *      Pendiente por comprar.
       *   2) Manual: variedad + longitud (caso excepcional, sin OC).
       */}
      <Modal
        open={creating}
        onClose={() => {
          setCreating(false)
          setVarietyLocked(null)
          setLinkedPoId('')
          setReceivedRows([])
        }}
        title={
          varietyLocked
            ? `Añadir longitud · ${varietyLocked}`
            : 'Añadir inventario'
        }
        subtitle={linkedPoId ? 'Recepción desde orden de compra' : 'Registra stock de una variedad y longitud'}
        size={linkedPoId ? 'xl' : 'md'}
      >
        <form onSubmit={onCreate} className="space-y-4">
          {!varietyLocked && (
            <Field
              label="Vincular a orden de compra"
              hint="Selecciona una OC para autollenar los items. Deja vacío para crear manualmente."
            >
              <Select
                value={linkedPoId}
                onChange={(e) => {
                  const id = e.target.value
                  setLinkedPoId(id)
                  const po = activePOs.find((p) => p.id === id)
                  if (po) {
                    setReceivedRows(
                      (po.items || []).map((it) => ({
                        variety: it.variety,
                        stemLength: it.stemLength,
                        ordered: Number(it.quantity) || 0,
                        received: Number(it.quantity) || 0,
                      })),
                    )
                  } else {
                    setReceivedRows([])
                  }
                }}
              >
                <option value="">— sin OC (ingreso manual) —</option>
                {activePOs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.id} · {p.supplierName || '—'} · entrega{' '}
                    {p.deliveryDate || p.expectedAt || '—'}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          {linkedPoId ? (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-expo-dark">
                Productos de la OC {linkedPoId}
              </div>
              <div className="rounded-xl border border-expo-line bg-white overflow-hidden">
                <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-expo-light/40 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  <div className="col-span-4">Variedad</div>
                  <div className="col-span-2">Longitud</div>
                  <div className="col-span-3 text-right">Pedido</div>
                  <div className="col-span-3 text-right">Recibido</div>
                </div>
                <div className="divide-y divide-gray-100">
                  {receivedRows.map((r, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-12 gap-2 px-3 py-2 text-sm items-center"
                    >
                      <div className="col-span-4 font-medium text-expo-dark">
                        {r.variety}
                      </div>
                      <div className="col-span-2 text-gray-600">
                        {r.stemLength}cm
                      </div>
                      <div className="col-span-3 text-right text-gray-700">
                        {fmtQty(r.ordered)}
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          min="0"
                          max={r.ordered}
                          value={r.received}
                          onChange={(e) => {
                            const v = e.target.value
                            setReceivedRows((rows) =>
                              rows.map((row, idx) =>
                                idx === i ? { ...row, received: v } : row,
                              ),
                            )
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <p className="text-[11px] text-gray-500">
                Si recibes menos cantidad de la pedida, el faltante vuelve a
                Pendiente por comprar automáticamente.
              </p>
            </div>
          ) : (
            <>
              <Field
                label="Variedad"
                required
                hint={
                  varietyLocked
                    ? 'La variedad ya existe — solo agregaremos una nueva longitud.'
                    : 'Escribe una nueva o elige una existente del autocompletar.'
                }
              >
                {varietyLocked ? (
                  <Input value={varietyLocked} readOnly disabled />
                ) : (
                  <>
                    <Input
                      name="variety"
                      list="inv-variety-suggestions"
                      placeholder="p. ej. Begonia"
                      required
                    />
                    <datalist id="inv-variety-suggestions">
                      {varietySuggestions.map((v) => (
                        <option key={v} value={v} />
                      ))}
                    </datalist>
                  </>
                )}
              </Field>
              <Field label="Longitud del tallo (cm)" required>
                <Input
                  type="number"
                  name="stemLength"
                  min="1"
                  defaultValue={60}
                  required
                />
              </Field>
              <div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Sin procesar">
                    <Input type="number" name="unprocessed" min="0" defaultValue={0} />
                  </Field>
                  <Field label="Procesado">
                    <Input type="number" name="cold_room" min="0" defaultValue={0} />
                  </Field>
                </div>
                <p className="text-[12px] mt-2" style={{ color: '#8B9B8F' }}>
                  Indica una cantidad inicial (en una de las dos etapas). Una longitud
                  con 0 tallos no aparece en el listado.
                </p>
              </div>
            </>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCreating(false)
                setVarietyLocked(null)
                setLinkedPoId('')
                setReceivedRows([])
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={create.loading || adjust.loading}>
              {create.loading || adjust.loading
                ? 'Guardando…'
                : linkedPoId
                  ? 'Registrar recepción'
                  : varietyLocked
                    ? 'Añadir longitud'
                    : 'Añadir inventario'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
